import { useEffect, useState } from "react";
import { getMasterSheet } from "@/services/report";
import { getUnidades } from "@/services/unidade";
import { getTurma } from "@/services/turma";
import { Table, ConfigProvider, theme, Select, Button } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { mascaraCpf, mascaraMatricula } from "@/utils/masks";
import "@/styles/globals.css";

interface MasterSheetItem {
  matricula: number | null;
  nomeAluno: string;
  cpfAluno: string;
  dddAluno: string;
  telefoneAluno: string;
  emailAluno: string;
  nomeResponsavel: string;
  cpfResponsavel: string;
  rg: string;
  orgaoEmissor: string;
  ufEmissor: string;
  dataEmissao: string | null;
  dddResponsavel: string;
  telefoneResponsavel: string;
  emailResponsavel: string;
  endereco: string;
  logradouro: string;
  complemento: string;
  numero: string;
  cep: string;
  localidade: string;
  bairro: string;
  especificas: string;
}

interface Opcao {
  id: number;
  nome: string;
}

// Valor sentinela da opção "Todos" nos filtros multi-seleção
const TODOS = "";

// Telefone já vem sem o DDD (separado pelo backend)
const formatarTelefone = (valor: string | null | undefined) => {
  if (!valor) return "";
  if (valor.length === 9) return valor.replace(/(\d{5})(\d{4})/, "$1-$2");
  if (valor.length === 8) return valor.replace(/(\d{4})(\d{4})/, "$1-$2");
  return valor;
};

export default function MasterSheet() {
  const [unidades, setUnidades] = useState<Opcao[]>([]);
  const [turmas, setTurmas] = useState<Opcao[]>([]);
  const [filtroUnidades, setFiltroUnidades] = useState<(number | string)[]>([TODOS]);
  const [filtroTurmas, setFiltroTurmas] = useState<(number | string)[]>([TODOS]);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<MasterSheetItem[]>([]);

  useEffect(() => {
    getUnidades().then((res) => setUnidades(res.data || []));
    getTurma().then((res) => setTurmas(res.data || []));
  }, []);

  // Mantém a opção "Todos" exclusiva: selecionar "Todos" limpa as demais e vice-versa
  const handleMultiSelect = (values: (number | string)[], setter: (v: (number | string)[]) => void) => {
    if (values.length === 0) {
      setter([TODOS]);
      return;
    }
    const ultimo = values[values.length - 1];
    if (ultimo === TODOS) {
      setter([TODOS]);
      return;
    }
    setter(values.filter((v) => v !== TODOS));
  };

  const buscar = async () => {
    setLoading(true);
    const params: { unidadeIds?: string; turmaIds?: string } = {};
    if (!filtroUnidades.includes(TODOS) && filtroUnidades.length > 0) {
      params.unidadeIds = filtroUnidades.join(",");
    }
    if (!filtroTurmas.includes(TODOS) && filtroTurmas.length > 0) {
      params.turmaIds = filtroTurmas.join(",");
    }
    const res = await getMasterSheet(params);
    setDados(res?.data || []);
    setLoading(false);
  };

  const descricaoUnidades = filtroUnidades.includes(TODOS)
    ? "Todas"
    : unidades.filter((u) => filtroUnidades.includes(u.id)).map((u) => u.nome).join(", ") || "Todas";

  const descricaoTurmas = filtroTurmas.includes(TODOS)
    ? "Todas"
    : turmas.filter((t) => filtroTurmas.includes(t.id)).map((t) => t.nome).join(", ") || "Todas";

  const montarLinhaExportacao = (item: MasterSheetItem) => ({
    "ID Aluno": item.matricula ? mascaraMatricula(item.matricula.toString()) : "",
    "Nome Aluno": item.nomeAluno || "",
    "CPF Aluno": item.cpfAluno ? mascaraCpf(item.cpfAluno) : "",
    "DDD Aluno": item.dddAluno || "",
    "Telefone Aluno": formatarTelefone(item.telefoneAluno),
    "Email Aluno": item.emailAluno || "",
    "Nome Responsável": item.nomeResponsavel || "",
    "CPF Respon.": item.cpfResponsavel ? mascaraCpf(item.cpfResponsavel) : "",
    "RG": item.rg || "",
    "Emissor": item.orgaoEmissor || "",
    "UF Emissor/Estado": item.ufEmissor || "",
    "DT Emissão": item.dataEmissao ? format(new Date(item.dataEmissao), "dd/MM/yyyy") : "",
    "DDD R.": item.dddResponsavel || "",
    "Tel. R.": formatarTelefone(item.telefoneResponsavel),
    "Email Respon.": item.emailResponsavel || "",
    "Endereço": item.endereco || "",
    "Logradouro": item.logradouro || "",
    "Complemento": item.complemento || "",
    "Número": item.numero || "",
    "CEP": item.cep || "",
    "Localidade": item.localidade || "",
    "Bairro": item.bairro || "",
    "ESPECÍFICAS/SERVIÇOS": item.especificas || "",
  });

  const exportToExcel = () => {
    const exportData = dados.map(montarLinhaExportacao);

    // Cabeçalho dos filtros
    const header = [[`Unidades: ${descricaoUnidades}`, `Turmas: ${descricaoTurmas}`]];
    const ws = XLSX.utils.aoa_to_sheet(header);
    XLSX.utils.sheet_add_json(ws, exportData, { origin: -1 });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MasterSheet");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "master_sheet.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", format: "a3" });
    doc.setFontSize(16);
    doc.text("MasterSheet", 14, 14);
    doc.setFontSize(11);
    doc.text(`Unidades: ${descricaoUnidades}`, 14, 22);
    doc.text(`Turmas: ${descricaoTurmas}`, 160, 22);

    const head = [
      "ID Aluno", "Nome Aluno", "CPF Aluno", "DDD Aluno", "Telefone Aluno", "Email Aluno",
      "Nome Responsável", "CPF Respon.", "RG", "Emissor", "UF Emissor/Estado", "DT Emissão",
      "DDD R.", "Tel. R.", "Email Respon.", "Endereço", "Logradouro", "Complemento",
      "Número", "CEP", "Localidade", "Bairro", "ESPECÍFICAS/SERVIÇOS",
    ];

    const tableData = dados.map((item) => Object.values(montarLinhaExportacao(item)).map(String));

    autoTable(doc, {
      head: [head],
      body: tableData,
      startY: 26,
      styles: { fontSize: 5, cellPadding: 1 },
      headStyles: { fontSize: 5 },
      margin: { left: 5, right: 5 },
      tableWidth: 'auto',
    });

    doc.save("master_sheet.pdf");
  };

  const columns: ColumnsType<MasterSheetItem> = [
    {
      title: "ID Aluno",
      dataIndex: "matricula",
      key: "matricula",
      width: 110,
      fixed: "left",
      align: "right",
      render: (matricula: number | null) => (matricula ? mascaraMatricula(matricula.toString()) : ""),
    },
    { title: "Nome Aluno", dataIndex: "nomeAluno", key: "nomeAluno", width: 200, fixed: "left" },
    {
      title: "CPF Aluno",
      dataIndex: "cpfAluno",
      key: "cpfAluno",
      width: 130,
      render: (cpf: string) => (cpf ? mascaraCpf(cpf) : ""),
    },
    { title: "DDD Aluno", dataIndex: "dddAluno", key: "dddAluno", width: 80 },
    {
      title: "Telefone Aluno",
      dataIndex: "telefoneAluno",
      key: "telefoneAluno",
      width: 120,
      render: (tel: string) => formatarTelefone(tel),
    },
    { title: "Email Aluno", dataIndex: "emailAluno", key: "emailAluno", width: 200 },
    { title: "Nome Responsável", dataIndex: "nomeResponsavel", key: "nomeResponsavel", width: 200 },
    {
      title: "CPF Respon.",
      dataIndex: "cpfResponsavel",
      key: "cpfResponsavel",
      width: 130,
      render: (cpf: string) => (cpf ? mascaraCpf(cpf) : ""),
    },
    { title: "RG", dataIndex: "rg", key: "rg", width: 110 },
    { title: "Emissor", dataIndex: "orgaoEmissor", key: "orgaoEmissor", width: 90 },
    { title: "UF Emissor/Estado", dataIndex: "ufEmissor", key: "ufEmissor", width: 90 },
    {
      title: "DT Emissão",
      dataIndex: "dataEmissao",
      key: "dataEmissao",
      width: 100,
      render: (date: string | null) => (date ? format(new Date(date), "dd/MM/yyyy") : ""),
    },
    { title: "DDD R.", dataIndex: "dddResponsavel", key: "dddResponsavel", width: 70 },
    {
      title: "Tel. R.",
      dataIndex: "telefoneResponsavel",
      key: "telefoneResponsavel",
      width: 110,
      render: (tel: string) => formatarTelefone(tel),
    },
    { title: "Email Respon.", dataIndex: "emailResponsavel", key: "emailResponsavel", width: 200 },
    { title: "Endereço", dataIndex: "endereco", key: "endereco", width: 280 },
    { title: "Logradouro", dataIndex: "logradouro", key: "logradouro", width: 200 },
    { title: "Complemento", dataIndex: "complemento", key: "complemento", width: 120 },
    { title: "Número", dataIndex: "numero", key: "numero", width: 80 },
    { title: "CEP", dataIndex: "cep", key: "cep", width: 100 },
    { title: "Localidade", dataIndex: "localidade", key: "localidade", width: 140 },
    { title: "Bairro", dataIndex: "bairro", key: "bairro", width: 140 },
    { title: "ESPECÍFICAS/SERVIÇOS", dataIndex: "especificas", key: "especificas", width: 260 },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">MasterSheet</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8">
          <Select
            mode="multiple"
            value={filtroUnidades}
            onChange={(values) => handleMultiSelect(values, setFiltroUnidades)}
            style={{ minWidth: 260 }}
            placeholder="Unidade"
            maxTagCount="responsive"
            options={[
              { value: TODOS, label: "Todas as Unidades" },
              ...unidades.map((u) => ({ value: u.id, label: u.nome })),
            ]}
          />
          <Select
            mode="multiple"
            value={filtroTurmas}
            onChange={(values) => handleMultiSelect(values, setFiltroTurmas)}
            style={{ minWidth: 260 }}
            placeholder="Turma"
            maxTagCount="responsive"
            options={[
              { value: TODOS, label: "Todas as Turmas" },
              ...turmas.map((t) => ({ value: t.id, label: t.nome })),
            ]}
          />
          <Button type="primary" loading={loading} onClick={buscar}>
            Buscar
          </Button>
          <Button onClick={exportToExcel} disabled={dados.length === 0}>Exportar Excel</Button>
          <Button onClick={exportToPDF} disabled={dados.length === 0}>Exportar PDF</Button>
        </div>
        <Table
          columns={columns}
          dataSource={dados}
          rowKey={(row, idx) => `${row.matricula ?? "sem-matricula"}-${idx}`}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 3000 }}
          locale={{ emptyText: "Nenhum dado encontrado." }}
        />
      </ConfigProvider>
    </>
  );
}
